/**
 * Shared helpers for RTDB services.
 *
 * RTDB stores dates as Unix milliseconds (numbers). These helpers convert
 * between JS Date objects (used in app types) and milliseconds (used in storage).
 *
 * Undefined values are not allowed in RTDB writes, so they must be stripped
 * before every write operation.
 */

/**
 * Convert a Date to Unix milliseconds for storage.
 * Passes through numbers (already converted) and null/undefined.
 */
export const dateToMs = (v: any): any => {
  if (v instanceof Date) return v.getTime();
  return v;
};

/**
 * Convert Unix milliseconds (or legacy Firestore Timestamp objects) to a Date.
 * Returns undefined for undefined/null input.
 */
export const msToDate = (v: any): Date | undefined => {
  if (v === undefined || v === null) return undefined;
  if (v instanceof Date) return v;
  // Legacy Firestore Timestamp shape { seconds, nanoseconds }
  if (typeof v === 'object' && typeof v.seconds === 'number') {
    return new Date(v.seconds * 1000 + (v.nanoseconds ?? 0) / 1e6);
  }
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
};

/**
 * Recursively strip undefined values from an object.
 * RTDB refuses writes containing undefined. Nested objects and arrays are
 * cleaned; arrays preserve their indices (undefined entries become null).
 */
export const stripUndefined = <T>(obj: T): T => {
  if (obj === undefined || obj === null) return obj;
  if (Array.isArray(obj)) {
    return obj.map((item) =>
      item === undefined ? null : stripUndefined(item)
    ) as unknown as T;
  }
  if (typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj as Record<string, any>)) {
      if (value === undefined) continue;
      result[key] = stripUndefined(value);
    }
    return result as T;
  }
  return obj;
};

/**
 * Convert an RTDB snapshot value (an object keyed by id) into an array,
 * attaching each key as the `id` field. Returns an empty array for null.
 */
export const snapshotToArray = <T>(
  value: Record<string, any> | null,
  mapper: (id: string, data: any) => T
): T[] => {
  if (!value) return [];
  return Object.entries(value).map(([id, data]) => mapper(id, data));
};
